import {
  Architecture,
  OsType,
  type Channel,
  type PackageVersion,
  type SourceDeploymentConfig,
  type Train,
  type VersionInfo,
} from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import dayjs from 'dayjs';
import {
  ChannelName,
  EngineOverrideKind,
  type EngineOverride,
  type SourceDeploymentConfigUpdate,
  type VersionsByPackage,
  type SyncConfigArtifactResponse,
  type SyncConfigChannelResponse,
  type SyncConfigGroupedArtifactsResponse,
  type SyncConfigListTrainsResponse,
  type SyncConfigSourceDeploymentResponse,
  type SyncConfigTrainResponse,
  type SyncConfigVersionInfoResponse,
} from './types';

export const getDisplayValue = (value: string | null | undefined): string => value?.trim() || 'N/A';

export const getErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : 'Unknown error');

export const getPackageVersionKey = (packageName: string, versionTag: string): string =>
  JSON.stringify([packageName, versionTag]);

export const toCamelCaseVersionInfo = (versionInfo?: VersionInfo): VersionInfo | undefined =>
  versionInfo
    ? {
        binaryName: versionInfo.binaryName,
        expectedSignature: versionInfo.expectedSignature,
        targetVersion: versionInfo.targetVersion,
        targetChannel: versionInfo.targetChannel,
        packageName: versionInfo.packageName,
        status: versionInfo.status,
        runningVersion: versionInfo.runningVersion,
      }
    : undefined;

export const buildSourceDeploymentConfigRequest = (
  sourceId: string,
  config: SourceDeploymentConfig,
  update: SourceDeploymentConfigUpdate = {}
) => {
  const isContainer = update.enginePin?.engine.syncEngineId === 'Container';

  return {
    sourceId,
    config: {
      syncEngines: config.syncEngines?.map(({ syncEngineId, versionInfo }) => ({
        syncEngineId,
        versionInfo: update.enginePin
          ? {
              ...toCamelCaseVersionInfo(versionInfo),
              targetVersion:
                !isContainer && syncEngineId === update.enginePin.engine.syncEngineId
                  ? update.enginePin.targetVersion
                  : versionInfo?.targetVersion,
            }
          : toCamelCaseVersionInfo(versionInfo),
      })),
      containerVersionInfo: update.enginePin
        ? config.containerVersionInfo
          ? {
              ...toCamelCaseVersionInfo(config.containerVersionInfo),
              targetVersion: isContainer ? update.enginePin.targetVersion : config.containerVersionInfo.targetVersion,
            }
          : undefined
        : toCamelCaseVersionInfo(config.containerVersionInfo),
      campaignOverrideId: config.campaignOverrideId,
      targetChannel: update.targetChannel ?? config.targetChannel,
      targetTrainId: update.targetTrainId ?? config.targetTrainId,
    },
  };
};

export const formatDate = (value?: string): string => {
  const date = dayjs(value);
  return value && date.isValid() ? date.format('DD MMM YYYY hh:mm A') : 'N/A';
};

export const normalizeChannel = (channel: SyncConfigChannelResponse): Channel | undefined => {
  const channelName = channel.channelName ?? channel.channel_name;

  if (!channelName) return undefined;

  return {
    ...channel,
    channelName,
    currentTrainId: channel.currentTrainId ?? channel.current_train_id,
    updatedAt: channel.updatedAt ?? channel.updated_at,
    channelPromotedBy: channel.channelPromotedBy ?? channel.channel_promoted_by,
  };
};

export const normalizeTrain = (train: SyncConfigTrainResponse): Train | undefined => {
  const trainId = train.trainId ?? train.train_id;

  if (!trainId) return undefined;

  return {
    ...train,
    trainId,
    trainName: train.trainName ?? train.train_name,
  };
};

export const normalizeArtifactOption = (artifact: SyncConfigArtifactResponse): PackageVersion | undefined => {
  const packageName = artifact.packageName ?? artifact.package_name;
  const versionTag = artifact.versionTag ?? artifact.version_tag;

  if (!packageName || !versionTag) return undefined;

  return { packageName, versionTag };
};

export const normalizeTrainForDisplay = (train: SyncConfigTrainResponse): Train | undefined => {
  const normalizedTrain = normalizeTrain(train);

  if (!normalizedTrain?.trainId) return undefined;

  return {
    trainId: normalizedTrain.trainId,
    trainName: normalizedTrain.trainName ?? '',
    promotedChannel: train.promotedChannel ?? train.promoted_channel ?? '',
    channelPromotedBy: train.channelPromotedBy ?? train.channel_promoted_by ?? '',
    createdBy: train.createdBy ?? train.created_by ?? '',
    createdAt: train.createdAt ?? train.created_at ?? '',
    artifacts: (train.artifacts ?? []).map((artifact) => ({
      artifactId: artifact.artifactId ?? artifact.artifact_id ?? '',
      packageName: artifact.packageName ?? artifact.package_name ?? '',
      versionTag: artifact.versionTag ?? artifact.version_tag ?? '',
      targetOs: artifact.targetOs ?? artifact.target_os ?? OsType.OS_TYPE_UNSPECIFIED,
      targetArch: artifact.targetArch ?? artifact.target_arch ?? Architecture.ARCHITECTURE_UNSPECIFIED,
      targetPms: artifact.targetPms ?? artifact.target_pms ?? '',
      gcsArtifactUrl: artifact.gcsArtifactUrl ?? artifact.gcs_artifact_url ?? '',
      sha256Checksum: artifact.sha256Checksum ?? artifact.sha256_checksum ?? '',
      createdAt: artifact.createdAt ?? artifact.created_at ?? '',
    })),
  };
};

const normalizeVersionInfo = (versionInfo: SyncConfigVersionInfoResponse): VersionInfo => ({
  ...versionInfo,
  binaryName: versionInfo.binaryName ?? versionInfo.binary_name,
  expectedSignature: versionInfo.expectedSignature ?? versionInfo.expected_signature,
  targetVersion: versionInfo.targetVersion ?? versionInfo.target_version,
  targetChannel: versionInfo.targetChannel ?? versionInfo.target_channel,
  packageName: versionInfo.packageName ?? versionInfo.package_name,
  runningVersion: versionInfo.runningVersion ?? versionInfo.running_version,
});

export const normalizeSourceDeployment = (config: SyncConfigSourceDeploymentResponse): SourceDeploymentConfig => ({
  ...config,
  sourceId: config.sourceId ?? config.source_id,
  targetChannel: config.targetChannel ?? config.target_channel,
  campaignOverrideId: config.campaignOverrideId ?? config.campaign_override_id,
  targetTrainId: config.targetTrainId ?? config.target_train_id,
  containerVersionInfo: config.containerVersionInfo
    ? normalizeVersionInfo(config.containerVersionInfo)
    : config.container_version_info
    ? normalizeVersionInfo(config.container_version_info)
    : undefined,
  syncEngines: (config.syncEngines ?? config.sync_engines ?? []).map((engine) => {
    const versionInfo = engine.versionInfo ?? engine.version_info;
    return {
      ...engine,
      syncEngineId: engine.syncEngineId ?? engine.sync_engine_id,
      versionInfo: versionInfo ? normalizeVersionInfo(versionInfo) : undefined,
    };
  }),
});

export const getEngineOverride = (
  versionInfo: VersionInfo | undefined,
  config: SourceDeploymentConfig
): EngineOverride => {
  if (versionInfo?.targetVersion) return { kind: EngineOverrideKind.ActivePin, version: versionInfo.targetVersion };
  if (config.targetTrainId) return { kind: EngineOverrideKind.SourcePolicy, label: 'Follows Train' };
  if (config.targetChannel) return { kind: EngineOverrideKind.SourcePolicy, label: 'Follows Channel' };
  return { kind: EngineOverrideKind.SourcePolicy, label: 'N/A' };
};

export const getTotalTrains = (response: SyncConfigListTrainsResponse | undefined): number | undefined =>
  response?.totalCount ?? response?.total_count;

export const getTotalArtifacts = (response: SyncConfigGroupedArtifactsResponse | undefined): number | undefined =>
  response?.totalCount ?? response?.total_count;

// Frontend-owned promotion + capability rules. There is no topology API — these encode
// the product's fixed pipeline (alpha → beta → stable) while treating every other channel
// name returned by the channels API as a dynamic pre-alpha channel.

// Well-known canonical channels. Any name not in this set is treated as "dynamic".
const CANONICAL_ORDER: readonly ChannelName[] = [ChannelName.ALPHA, ChannelName.BETA, ChannelName.STABLE];
const CANONICAL_SET = new Set<ChannelName>(CANONICAL_ORDER);

export const isDynamicChannel = (name: ChannelName): boolean => !CANONICAL_SET.has(name);

export const getPromotionTarget = (name: ChannelName): ChannelName | undefined => {
  if (name === ChannelName.ALPHA) return ChannelName.BETA;
  if (name === ChannelName.BETA) return ChannelName.STABLE;
  if (name === ChannelName.STABLE) return undefined;
  return ChannelName.ALPHA;
};

export const supportsDirectReplace = (name: ChannelName): boolean => {
  return name !== ChannelName.BETA && name !== ChannelName.STABLE;
};

// Column order: alpha, beta, stable (skipping any canonical channel that isn't present in the
// response) followed by dynamic channels in channels-response order.
export const orderChannelsForDisplay = (channels: Channel[]): Channel[] => {
  const byName = new Map<ChannelName, Channel>();
  for (const channel of channels) byName.set(channel.channelName ?? '', channel);

  const canonical = CANONICAL_ORDER.map((name) => byName.get(name)).filter((c): c is Channel => c !== undefined);
  const dynamic = channels.filter((c) => isDynamicChannel(c.channelName ?? ''));

  return [...canonical, ...dynamic];
};

export const buildVersionsByPackage = (artifacts: PackageVersion[]): VersionsByPackage => {
  const map: VersionsByPackage = new Map();
  for (const { packageName, versionTag } of artifacts) {
    if (!packageName || !versionTag) continue;

    const versions = map.get(packageName) ?? new Set<string>();
    versions.add(versionTag);
    map.set(packageName, versions);
  }
  return map;
};

export const listPackageNames = (map: VersionsByPackage): string[] => Array.from(map.keys()).sort();
